import React, { useState } from 'react';
import ExperienceSummary from '../../components/ExperienceSummary';
import FieldFillerCard from '../../components/FieldFillerCard';
import { keyFromLabel } from '../../../../../../../../shared/lib/utils';
import { validateField } from '../../components/FieldFillerItem';
import { useForcedCartCache } from '../../context/CartCacheContext';
import { StepPropsBase } from '../Reserve';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import Alert from '../../components/Alert';
import { useForcedEventCache } from '../../context/EventCacheContext';
import { evaluateAudienceZone } from '../../../../../../../../shared/lib/audience';

type ReservationEntryViewProps = StepPropsBase;

export default function ReservationEntryView(props: ReservationEntryViewProps) {
  const { onBack, onNext } = props;

  const { event, allEvents } = useForcedEventCache();
  const { activeDraft, saveCart, passiveUpdateReservationField } = useForcedCartCache();
  const [loading, setLoading] = useState(false);

  const draft = activeDraft.reservationDraft;
  const draftTemplate = draft?.eventSnapshot;
  const reservableFields = draftTemplate?.reservables.fields ?? [];

  const reservationValues = draft.values ?? {};

  const canContinue = reservableFields.every(field => {
    const value =
      reservationValues[keyFromLabel(field.label)] ?? reservationValues[field.label] ?? '';
    return validateField(field, value).length === 0;
  });

  async function handleOnNext() {
    setLoading(true);

    try {
      if (!canContinue) return;
      await saveCart();
      onNext?.();
    } finally {
      setLoading(false);
    }
  }

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const visibleZones = dataCollections.zones
    .filter(z => evaluateAudienceZone('visible', z._id, undefined, event.eventTemplate.audience))
    .map(z => (
      <div
        key={z._id}
        className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
        style={{ backgroundColor: z.color }}
      >
        {z.name}
      </div>
    ));

  return (
    <TwoColumnLayout
      title={draftTemplate?.title}
      tags={visibleZones}
      subtitle="Reservation Details"
      aside={
        <ExperienceSummary
          nextDisabled={!canContinue}
          onBack={() => onBack?.()}
          onNext={handleOnNext}
          loading={loading}
        />
      }
    >
      {reservableFields.length > 0 ? (
        <FieldFillerCard
          title="Reservation Information"
          fields={reservableFields}
          values={reservationValues}
          onChangeField={(field, value) => {
            passiveUpdateReservationField(field.label, value);
          }}
        />
      ) : (
        <Alert>
          No reservation details required for this experience. You can continue to the next step.
        </Alert>
      )}
    </TwoColumnLayout>
  );
}
